import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, Validators, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RegisterService } from '../services/register.service';
import {
	Subject,
	of,
	catchError,
	debounceTime,
	distinctUntilChanged,
	filter,
	switchMap,
	take,
	takeUntil,
	tap,
} from 'rxjs';
import { SocialUser, SocialAuthService } from '@abacritt/angularx-social-login';
import { Store } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import { ReferrerDialogComponent } from './referrer-dialog/referrer-dialog.component';
import { authentication } from '@biz-action/user-actions/auth-login.actions';
import { MatStepper } from '@angular/material/stepper';

@Component({
	selector: 'app-register',
	templateUrl: './register.component.html',
	styleUrls: ['./register.component.scss'],
})
export class RegisterComponent implements OnInit, OnDestroy {
	private _isDestroyed: Subject<boolean> = new Subject<boolean>();
	@ViewChild('stepper') stepper: MatStepper;
	firstStepForm: FormGroup;
	secondStepForm: FormGroup;

	easystore = false;
	user: SocialUser;
	loggedIn: boolean;
	apiError: any = {};
	validating = false;
	progress = 0;
	text_num: number;

	constructor(
		private formBuilder: UntypedFormBuilder,
		private translate: TranslateService,
		private route: ActivatedRoute,
		private router: Router,
		private authService: SocialAuthService,
		private registerService: RegisterService,
		private store: Store,
		private dialog: MatDialog,
	) {
		this.authService;
	}

	get firstStepFormControls() {
		return this.firstStepForm.controls;
	}

	get secondStepFormControls() {
		return this.secondStepForm.controls;
	}

	ngOnDestroy(): void {
		this._isDestroyed.next(true);
		this._isDestroyed.complete();
	}

	ngOnInit(): void {
		this.firstStepForm = this.formBuilder.group({
			name: ['', Validators.required],
			email: ['', [Validators.required, Validators.email]],
			hp: ['', [Validators.pattern('^[+]?[0-9]{1,5}-?[0-9]{7,8}$'), Validators.required]],
			password: ['', [Validators.required, Validators.minLength(6)]],
			password_confirmation: ['', [Validators.required]]
		}, { validators: this.passwordMatchValidator });


		this.secondStepForm = this.formBuilder.group({
			company_name: ['', Validators.required],
			referrer_name: [''],
			referrer_phone: [''],
			subdomain: ['', [Validators.required, Validators.pattern('[a-z][a-z-0-9]*$')]],
			locale: [''],
			token: [''],
			provider: ['website'],
			utm_source: [''],
			utm_medium: [''],
			utm_campaign: [''],

			//Partner
			ref: [''],
			reseller: [''],
			partner: [''],
			agent: [''],

			//Referral Candy
			aic: [''],

			//Referral
			referral_id: [''],
		});

		this.route.queryParamMap.pipe(takeUntil(this._isDestroyed)).subscribe((params) => {
			this.secondStepForm.patchValue({
				//UTM
				utm_source: params.get('utm_source'),
				utm_medium: params.get('utm_medium'),
				utm_campaign: params.get('utm_campaign'),

				//Partner
				ref: params.get('ref'),
				reseller: params.get('reseller'),
				partner: params.get('partner'),
				agent: params.get('agent'),

				//Referral Candy
				aic: params.get('aic'),

				//Referral
				referral_id: params.get('referral_id'),

				// Set Domain Name
				domain_name: params.get('domain_name'),
			});

			if (params.get('utm_source') == 'easystore') {
				this.easystore = true;
			}
			if (params.get('aic')) {
				this.secondStepForm.get('referrer_name').patchValue(params.get('aic'));
			}
			if (params.get('name')) {
				this.firstStepForm.get('name').patchValue(params.get('name'));
			}
			if (params.get('email')) {
				this.firstStepForm.get('email').patchValue(params.get('email'));
			}
			if (params.get('password')) {
				this.firstStepForm.get('password').patchValue(params.get('password'));
				this.firstStepForm.get('password_confirmation').patchValue(params.get('password_confirmation'));
			}
			if (params.get('company_name')) {
				this.secondStepForm.get('company_name').patchValue(params.get('company_name'));
			}
		});
		this.authService.authState.pipe(takeUntil(this._isDestroyed)).subscribe((user) => {
			this.user = user;
			this.loggedIn = user != null;

			if (this.user) {
				this.secondStepForm.reset();
				this.firstStepForm.reset();
				this.firstStepForm.patchValue({
					name: this.user.firstName + ' ' + this.user.lastName,
					email: this.user.email,
				});
				this.firstStepForm.get('password').clearValidators();
				this.firstStepForm.get('password').updateValueAndValidity();
				this.firstStepForm.get('password_confirmation').clearValidators();
				this.firstStepForm.get('password_confirmation').updateValueAndValidity();
				this.firstStepForm.get('hp').clearValidators();
				this.firstStepForm.get('hp').updateValueAndValidity();

				this.secondStepForm.patchValue({
					provider: this.user.provider,
					token: this.user.provider == 'GOOGLE' ? this.user.idToken : this.user.authToken,
				});
				setTimeout(() => {
					this.stepper.next();
				}, 100);
			}
		});
		this.secondStepForm
			.get('subdomain')
			.valueChanges.pipe(
				takeUntil(this._isDestroyed),
				debounceTime(400),
				distinctUntilChanged(),
				tap(() => {
					this.apiError.subdomain = '';
					this.secondStepForm.get('subdomain').setErrors(null);
				}),
				filter((u) => !!u),
				tap(() => (this.validating = true)),
				switchMap((q) =>
					this.registerService.checkSubdomain({ subdomain: q }).pipe(
						catchError((err) => {
							this.apiError.subdomain = err.error.message;
							this.secondStepForm.get('subdomain').setErrors(
								{
									incorrect: true,
								},
								{ emitEvent: false },
							);
							return of(err);
						}),
						tap({
							complete: () => (this.validating = false),
						}),
					),
				),
			)
			.subscribe();
	}

	autoSubdomainName(event: Event) {
		let v = (<HTMLInputElement>event.target).value,
			ctrl = this.secondStepForm.get('subdomain');
		if (!v.length) {
			return;
		}
		// quit if subdomain has user-defined
		if (ctrl.dirty) {
			return;
		}
		ctrl.setValue(
			v
				.replace(/[\W\s]+/g, '')
				.slice(0, 10)
				.toLowerCase(),
		);
	}

	onSubmit() {
		this.firstStepForm.markAllAsTouched();
		this.secondStepForm.markAllAsTouched();
		setInterval(() => {
			this.text_num = Math.floor(Math.random() * 3) + 1;
		}, 3500);
		if (this.secondStepForm.invalid) {
			this.progress = 0;
			return;
		};
		if (!this.secondStepForm.get('referrer_name').value) {
			const dialogRef = this.dialog.open(ReferrerDialogComponent, {
				width: '40%',
				minWidth: '300px',
				maxWidth: '600px',
				panelClass: 'custom-container',
				autoFocus: false,
			});
			dialogRef
				.afterClosed()
				.pipe(
					take(1),
					tap((result) => {
						if (result === 'close') return;
						else if (result) {
							this.secondStepForm.get('referrer_name').patchValue(result.referrer_name);
							this.secondStepForm.get('referrer_phone').patchValue(result.referrer_phone);
						}
						this._proceedTOSubmit();
					}),
				)
				.subscribe();
		} else {
			this._proceedTOSubmit();
		}
	}

	private _proceedTOSubmit() {
		this.secondStepForm.get('locale').setValue(this.translate.currentLang);
		this.progress = 10;
		if (this.secondStepForm.invalid) {
			this.secondStepForm.markAllAsTouched();
			return;
		}
		if (this.secondStepForm.valid) {
			const payload = {
				...this.firstStepForm.value,
				...this.secondStepForm.value,
			};

			this.registerService
				.register(payload)
				.pipe(takeUntil(this._isDestroyed))
				.subscribe({
					next: (resp: any) => {
						if (resp.code_status == 200 && resp.data) {
							this.progress = 100;
							this.router.navigate([
								'register/complete',
								{
									subdomain: resp.data.subdomain,
									token: resp.data.token,
									email: this.firstStepForm.controls['email'].value,
								},
							]);
						}
					},
					error: (err: any) => {
						this.apiError = [];
						if (err.code_status == 422 || err.status == 422) {
							this.progress = 100;
							if (err.error.data.subdomain) {
								this.apiError.subdomain = err.error.data.subdomain[0];
							}
							if (err.error.data.email) {
								this.apiError.email = err.error.data.email[0];
							}
							if (err.error.data.password) {
								this.apiError.password = err.error.data.password[0];
							}
						}
					},
				});
		}
	}

	signInWithFB(): void {
		this.store.dispatch(authentication.fblogin());
	}

	reset() {
		this.firstStepForm.reset();
		this.secondStepForm.reset({
			provider: ['website'],
		});
	}

	private passwordMatchValidator(formGroup: FormGroup) {
		const password = formGroup.get('password').value;
		const confirmPassword = formGroup.get('password_confirmation').value;

		if (password !== confirmPassword) {
			formGroup.get('password_confirmation').setErrors({ passwordMismatch: true });
		} else {
			formGroup.get('password_confirmation').setErrors(null);
		}
	}
}
